/**
 * Single source of truth for page-level visibility toggles.
 * Each entry maps to a sidebar item AND blocks direct URL access when toggled off.
 *
 * - `key`     stable identifier stored on user.page_visibility[key]
 * - `label`   shown in the Permissions UI
 * - `paths`   URL prefixes covered by this toggle (used by ProtectedPageRoute)
 *
 * Admin / super_user always bypass these toggles. Other users see a page when
 * either (a) the toggle is missing/true, or (b) they're admin/super_user.
 */
export const PAGE_VISIBILITY_ITEMS = [
  { key: 'dashboard',       label: 'Dashboard',        paths: ['/', '/dashboard'] },
  { key: 'reminders',       label: 'Reminders',        paths: ['/reminders'] },
  { key: 'my_certs',        label: 'My Certs',         paths: ['/my-certifications'] },
  { key: 'contacts',        label: 'Contacts',         paths: ['/people'] },
  { key: 'companies',       label: 'Companies',        paths: ['/companies'] },
  { key: 'opportunities',   label: 'Opportunities',    paths: ['/opportunities'] },
  { key: 'proposals',       label: 'Proposals',        paths: ['/proposals'] },
  { key: 'jobs',            label: 'Jobs',             paths: ['/jobs'] },
  { key: 'calendar',        label: 'Calendar',         paths: ['/calendar'] },
  { key: 'timesheet',       label: 'Timesheet',        paths: ['/timesheet'] },
  { key: 'time_tracker',    label: 'Time Tracker',     paths: ['/field-tracker-v2'] },
  { key: 'field_reports',   label: 'Field Reports',    paths: ['/field-reports'] },
  { key: 'my_jobs',         label: 'My Jobs',          paths: ['/my-jobs'] },
  { key: 'recurring_jobs',  label: 'Recurring Jobs',   paths: ['/recurring-jobs'] },
  { key: '811_info',        label: '811 Info',         paths: ['/811-info'] },
  { key: 'reports',         label: 'Reports',          paths: ['/reports'] },
  { key: 'expenses',        label: 'Expenses',         paths: ['/expense-reports'] },
  { key: 'office',          label: 'Office',           paths: ['/office'] },
  { key: 'team',            label: 'Team',             paths: ['/team'] },
  { key: 'certifications',  label: 'Certifications',   paths: ['/certifications'] },
  { key: 'maintenance',     label: 'Maintenance',      paths: ['/maintenance'] },
  { key: 'archive',         label: 'Archive',          paths: ['/archive'] },
  { key: 'user_manual',     label: 'User Manual',      paths: ['/api/export/user-manual-html'] },
];

/** Default — every page visible. Only stores explicit "false" overrides. */
export function pageIsVisible(user, key) {
  if (!user) return true;
  if (user.role === 'admin' || user.role === 'super_user') return true;
  const v = user.page_visibility;
  // missing key / undefined → visible (sensible default)
  if (!v || v[key] === undefined || v[key] === null) return true;
  return v[key] !== false;
}

/** Find which page-key a given URL belongs to. Returns null if no match. */
export function pageKeyForPath(pathname) {
  if (!pathname) return null;
  // exact match for "/" → dashboard
  if (pathname === '/' || pathname === '/dashboard') return 'dashboard';
  for (const item of PAGE_VISIBILITY_ITEMS) {
    for (const p of item.paths) {
      if (p === '/' || p === '/dashboard') continue; // handled above
      if (pathname === p || pathname.startsWith(`${p}/`)) {
        return item.key;
      }
    }
  }
  return null;
}
